import { useForm } from "react-hook-form";
import usePartDefaultCategories from "../../data/usePartDefaultCategories";
import { FormatType, ListingDuration } from "../../models/EbayOffer";
import { IPart, IPartCategory, IPartCategoryDefault, TitleCase } from "../../models/Part";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useEbay from "../../data/useEbay";
import { useEffect, useState } from "react";
import SelectInput from "../shared/Inputs/SelectInput";
import { EbayCategoryDropdown } from "../ebayListings/EbayCategoryDropdown";
import { EbayStoreCategoryDropdown } from "../ebayListings/EbayStoreCategoryDropdown";
import { NumberInput } from "../shared/Inputs/NumberInput";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import EbayListingTitle from "../ebayListings/EbayListingTitle";
import LoadingWheel from "../shared/LoadingWheel";
import { TextInput } from "../shared/Inputs/TextInput";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import useNotification from "../notifications/useNotifications";
import usePartDefaultCategoriesMutations from "../../data/usePartDefaultCategoriesMutations";
import usePartPro from "../../data/usePartPro";

const formatOptions = [
  { id: FormatType.Auction, name: "Auction" },
  { id: FormatType.FixedPrice, name: "Buy It Now" },
];

const durationOptions = [
  // { id: ListingDuration.GTC, name: "Until Cancelled" }, //auto selected on 
  { id: ListingDuration.Days1, name: "1 Day" },
  { id: ListingDuration.Days3, name: "3 Days" },
  { id: ListingDuration.Days5, name: "5 Days" },
  { id: ListingDuration.Days7, name: "7 Days" },
  { id: ListingDuration.Days10, name: "10 Days" },
  { id: ListingDuration.Days21, name: "21 Days" },
  { id: ListingDuration.Days30, name: "30 Days" },
];

const titleCaseOptions = [
  { id: TitleCase["No Change"], name: "No Change" },
  { id: TitleCase.Upper, name: "Upper" },
  { id: TitleCase.Sentence, name: "Sentence" },
];


export function CategoryListingDefaults({ category }: { category: IPartCategory | undefined }) {
  const { defaultPartCategory, saveDefaultCategory } = usePartDefaultCategories({ part: { categoryId: category?.id } as IPart });
  const { register, getValues, setValue, watch, handleSubmit, reset, control } = useForm<IPartCategoryDefault>();
  const { saveHandlers } = useDefaultCRUDHandlers("Category Defaults");
  const { addNotification } = useNotification();
  const { policies, locations } = useEbay();
  const { bulkApplyCategoryDefaultsToUnlisted } = usePartDefaultCategoriesMutations();
  const {
    connection: partProConnection,
    categories: partProCategories,
    shippingPolicies: partProShippingPolicies,
  } = usePartPro();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [applyDefaultsOpen, setApplyDefaultsOpen] = useState<boolean>(false);
  const [isApplyingDefaults, setIsApplyingDefaults] = useState<boolean>(false);
  const selectedPartProShippingPolicyId = watch("partProShippingPolicyId");

  // Keep legacy band/pence in sync when a PartPro policy is chosen (PartPro still resolves from id).
  useEffect(() => {
    if (!selectedPartProShippingPolicyId) return;
    const policy = (partProShippingPolicies.data?.data ?? []).find(
      (p) => p.id === selectedPartProShippingPolicyId
    );
    if (!policy) return;
    setValue("partProShippingBand", policy.shippingBand);
    setValue(
      "partProShippingPricePence",
      policy.shippingBand === "COLLECTION_ONLY" ? 0 : policy.pricePence
    );
  }, [selectedPartProShippingPolicyId, partProShippingPolicies.data?.data, setValue]);

  const handleSetFormat = (_: string, value: any) => {
    setValue(
      "duration",
      (value === FormatType.FixedPrice
        ? ListingDuration.GTC as number
        : ListingDuration.Days30 as number)
    );
    setValue("format", value);
  };

  const handleApplyDefaultsToUnlisted = async () => {
    if (!category?.id) return;

    setIsApplyingDefaults(true);

    try {
      const response: any = await bulkApplyCategoryDefaultsToUnlisted(category.id);

      setApplyDefaultsOpen(false);
      addNotification({
        variant: "success",
        primaryText: "Applied category defaults to unlisted offers",
      });
    } catch {
      addNotification({
        variant: "error",
        primaryText: "Failed to apply defaults to unlisted offers",
      });
    } finally {
      setIsApplyingDefaults(false);
    }
  };

  const onSubmit = (data: IPartCategoryDefault) => {
    saveDefaultCategory.mutate({
      ...data,
      // ebayCategoryId: getValues("ebayCategoryId"),
    }, {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        setApplyDefaultsOpen(true);
      },
    });
  };

  useEffect(() => {
    if (!defaultPartCategory.isLoading && defaultPartCategory.data) {
      let data = defaultPartCategory.data?.data;

      if (!data) {
        reset({
          width: 0,
          height: 0,
          length: 0,
          weight: 0,
          format: FormatType.FixedPrice,
          duration: ListingDuration.GTC,
          auctionReservePrice: 0,
          auctionStartPrice: 0,
          buyItNowPrice: 0,
          enableBestOffer: false,
          autoAcceptPrice: 0,
          autoAcceptThreshold: 0,
          fulfillmentPolicyId: "",
          paymentPolicyId: "",
          returnPolicyId: "",
          ebayCategoryId: "",
          ebayStoreCategoryId: "",
          partProCategoryId: "",
          partProShippingPolicyId: "",
          partProShippingBand: "",
          partProShippingPricePence: null,
          locationKey: "",
          titleStructure: "",
          titleCase: TitleCase["No Change"],
          categoryAlias: "",
        });

        setIsLoading(false);
        return;
      }

      reset({
        width: data.width ?? 0,
        height: data.height ?? 0,
        length: data.length ?? 0,
        weight: data.weight ?? 0,
        format: data.format ?? FormatType.FixedPrice,
        duration: data.duration ?? ListingDuration.GTC,
        auctionReservePrice: data.auctionReservePrice ?? 0,
        auctionStartPrice: data.auctionStartPrice ?? 0,
        buyItNowPrice: data.buyItNowPrice ?? 0,
        enableBestOffer: data.enableBestOffer ?? false,
        autoAcceptPrice: data.autoAcceptPrice ?? 0,
        autoAcceptThreshold: data.autoAcceptThreshold ?? 0,
        fulfillmentPolicyId: data.fulfillmentPolicyId,
        paymentPolicyId: data.paymentPolicyId,
        returnPolicyId: data.returnPolicyId,
        locationKey: data.locationKey,
        ebayCategoryId: data.ebayCategoryId,
        ebayStoreCategoryId: data.ebayStoreCategoryId,
        partProCategoryId: data.partProCategoryId ?? "",
        partProShippingPolicyId: data.partProShippingPolicyId ?? "",
        partProShippingBand: data.partProShippingBand ?? "",
        partProShippingPricePence: data.partProShippingPricePence ?? null,
        titleStructure: data.titleStructure ?? "{{year}} {{make}} {{shortModel}} {{categoryName}}",
        titleCase: data.titleCase ?? TitleCase["No Change"],
        categoryAlias: data.categoryAlias ?? "",
      });

      setIsLoading(false);
    }
  }, [defaultPartCategory.isLoading, defaultPartCategory.data, category]);

  if (isLoading)
    return <LoadingWheel />;

  return (
    <>
      <ConfirmModal
        open={applyDefaultsOpen}
        setOpen={setApplyDefaultsOpen}
        isLoading={isApplyingDefaults}
        title={"Apply Defaults to Unlisted Offers?"}
        message={`Defaults have been saved.\nWould you like to update all items in this category that are awaiting listing with the new defaults?`}
        confirmButtonText={"Apply Defaults"}
        onConfirm={handleApplyDefaultsToUnlisted}
      />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 ">
        <h1 className="text-lg">Category Listing Defaults</h1>
        <div className="border-t border-gray-200 pt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-2">

        <div className="flex-col space-y-2 sm:col-span-2">
          <EbayListingTitle
            register={register}
            getValues={getValues}
            setValue={setValue}
            registerName={"titleStructure"}
            isTemplate={true}
            templateCategory={category}
          />
          {(defaultPartCategory.data?.data && !defaultPartCategory.data?.data.titleStructure) && (
            <p className="pt-2 text-sm text-red-600">Title Structure Not Saved</p>
          )}
        </div>

        <TextInput
          label={"Category Alias"}
          register={register}
          registerName={"categoryAlias"}
          placeholder={"Enter category alias..."}
        />

        <SelectInput
          label={"Title Case"}
          register={register}
          registerName={"titleCase"}
          options={titleCaseOptions}
          setValue={setValue}
          watch={watch}
        />

        <SelectInput
          label={"Listing Format"}
          register={register}
          registerName={"format"}
          options={formatOptions}
          setValue={handleSetFormat}
          watch={watch}
        />

        {getValues("format") == FormatType.Auction ? (
          <SelectInput
            label={"Duration"}
            register={register}
            registerName={"duration"}
            options={durationOptions}
            setValue={setValue}
            watch={watch}
          />
        ) : (
          <div className="sm:col-span-1 h-16">{/* Spacer */}</div>
        )}

        <EbayCategoryDropdown
          categoryId={defaultPartCategory.data?.data?.ebayCategoryId ?? ""}
          register={register}
          registerName={"ebayCategoryId"}
          setValue={setValue}
          watch={watch}
        />

        <EbayStoreCategoryDropdown
          categoryId={defaultPartCategory.data?.data?.ebayStoreCategoryId ?? ""}
          register={register}
          registerName={"ebayStoreCategoryId"}
          setValue={setValue}
          watch={watch}
        />

        <SelectInput
          label={"PartPro Category"}
          register={register}
          registerName={"partProCategoryId"}
          options={[
            { id: "", name: partProConnection.data?.data?.connected ? "None (use eBay mapping)" : "Connect PartPro first" },
            ...(() => {
              const cats = partProCategories.data?.data ?? [];
              const byId = new Map(cats.map((c) => [c.id, c]));
              // Prefer leaves; show "Main → Leaf". Keep selectable mains that have no children (e.g. Mirrors).
              const childIds = new Set(cats.filter((c) => c.parentId).map((c) => c.parentId as string));
              return cats
                .filter((c) => c.parentId || !childIds.has(c.id))
                .map((c) => {
                  const parent = c.parentId ? byId.get(c.parentId) : undefined;
                  const grand = parent?.parentId ? byId.get(parent.parentId) : undefined;
                  const label = grand
                    ? `${grand.name} → ${parent!.name} → ${c.name}`
                    : parent
                      ? `${parent.name} → ${c.name}`
                      : c.name;
                  return { id: c.id, name: label };
                })
                .sort((a, b) => a.name.localeCompare(b.name));
            })(),
          ]}
          setValue={setValue}
          watch={watch}
        />

        <SelectInput
          label={"PartPro Shipping Policy"}
          register={register}
          registerName={"partProShippingPolicyId"}
          options={[
            {
              id: "",
              name: partProConnection.data?.data?.connected
                ? "None (use weight defaults)"
                : "Connect PartPro first",
            },
            ...(partProShippingPolicies.data?.data ?? []).map((p) => {
              const priceLabel =
                p.shippingBand === "COLLECTION_ONLY"
                  ? "collection"
                  : p.pricePence === 0
                    ? "free"
                    : `£${(p.pricePence / 100).toFixed(2)}`;
              return { id: p.id, name: `${p.name} (${priceLabel})` };
            }),
          ]}
          setValue={setValue}
          watch={watch}
        />

        {getValues("format") == FormatType.Auction ? (
          <>
            <NumberInput
              label={"Starting Price (£)"}
              register={register}
              registerName={"auctionStartPrice"}
            />
            <NumberInput
              label={"Reserve Price (£)"}
              register={register}
              registerName={"auctionReservePrice"}
            />
          </>
        ) : (
          <>
            <NumberInput
              label={"Buy It Now Price (£)"}
              register={register}
              registerName={"buyItNowPrice"}
            />

            <NumberInput
              label={"Best Offer Auto Accept Threshold (%) - If no Best Offer Auto Accept Price then % Below listing price to auto accept"}
              register={register}
              registerName={"autoAcceptThreshold"}
            />

            {watch("enableBestOffer") && (
              <>
                <NumberInput
                  label={"Best Offer Auto Accept Price (£)"}
                  register={register}
                  registerName={"autoAcceptPrice"}
                />
                <div className="sm:col-span-1 h-16">{/* Spacer */}</div>
              </>
            )}
          </>
        )}

        {getValues("format") == FormatType.FixedPrice && (
          <>
            <div className="sm:col-span-1 mt-4">
              <input
                id="enableBestOffer"
                {...register("enableBestOffer")}
                type="checkbox"
                className="mr-2 h-5 w-5"
              />
              <label htmlFor="enableBestOffer">Allow Best Offer?</label>
            </div>

            <div className="sm:col-span-1 h-16">{/* Spacer */}</div>
          </>
        )}

        <SelectInput
          label={"Shipping Policy*"}
          register={register}
          registerName={"fulfillmentPolicyId"}
          options={policies.data?.data.fulfilmentPolicies ?? []}
          setValue={setValue}
          watch={watch}
        />
        <SelectInput
          label={"Payment Policy*"}
          register={register}
          registerName={"paymentPolicyId"}
          options={policies.data?.data.paymentPolicies ?? []}
          setValue={setValue}
          watch={watch}
        />
        <SelectInput
          label={"Returns Policy*"}
          register={register}
          registerName={"returnPolicyId"}
          options={policies.data?.data.returnPolicies ?? []}
          setValue={setValue}
          watch={watch}
        />

        <SelectInput
          label={"Location*"}
          register={register}
          registerName={"locationKey"}
          options={locations.data?.data.locations ?? []}
          setValue={setValue}
          watch={watch}
        />

        <NumberInput label="Width (cm)" registerName="width" register={register} step="1" />
        <NumberInput label="Height (cm)" registerName="height" register={register} step="1" />
        <NumberInput label="Length (cm)" registerName="length" register={register} step="1" />
        <NumberInput label="Weight (kg)" registerName="weight" register={register} step="1" />

          <ButtonInput label={"Save"} isSubmit={true} onClick={undefined} classes={"w-1/2"} />
        </div>
      </form>
    </>
  );
}